const EXPECTED_HOST = 'cabinet.tax.gov.ua'
const REQUIRED_PARAMS = ['date', 'time', 'fn', 'id', 'sm']
const OPTIONAL_PARAMS = ['mac']

export const RECEIPT_QR_ERROR = {
  WRONG_HOST: 'wrong_host',
  MISSING_PARAMS: 'missing_params',
  NOT_URL: 'not_url',
}

function formatDateYmdToIso(ymd) {
  if (!/^\d{8}$/.test(ymd)) return ''
  return `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}`
}

// Чеки з різних РРО зашивають час у QR неуніфіковано: cabinet.tax.gov.ua
// приймає і `time=20:19:09`, і `time=1420` (HHMM без секунд), і `time=142000`.
// Нормалізуємо у HH:MM:SS, щоб і поле часу, і відправка на бекенд завжди мали
// один формат. Невалідне (поза межами 23:59:59 або не цифри) — повертаємо як є,
// валідація на формі покаже помилку формату.
function normalizeQrTime(raw) {
  if (typeof raw !== 'string') return ''
  const t = raw.trim()
  if (!t) return ''
  if (/^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(t)) return t
  if (/^([01]\d|2[0-3]):[0-5]\d$/.test(t)) return `${t}:00`
  if (/^\d{6}$/.test(t)) {
    const h = t.slice(0, 2), m = t.slice(2, 4), s = t.slice(4, 6)
    if (Number(h) <= 23 && Number(m) <= 59 && Number(s) <= 59) {
      return `${h}:${m}:${s}`
    }
  }
  if (/^\d{4}$/.test(t)) {
    const h = t.slice(0, 2), m = t.slice(2, 4)
    if (Number(h) <= 23 && Number(m) <= 59) {
      return `${h}:${m}:00`
    }
  }
  return t
}

// Деякі касові системи зашивають суму з роздільником тисяч (напр. `1 104.90`).
// Роздільник у дикій природі буває різним: звичайний пробіл, нерозривний U+00A0,
// тонкі U+2009/U+202F, з QR ще й %20/`+`, а на реальних чеках трапляється навіть
// «mojibake» — нерозривний пробіл, двічі перекодований у UTF-8 (байти c3 82 c2 a0),
// який QR-рідер віддає браузеру як `Â`+U+00A0, тобто символ `Â` (U+00C2) вже НЕ
// пробіл і `\s` його не прибирає. Поле валідує суму як `^\d+(\.\d{1,2})?$`, тож
// будь-який зайвий символ її ламає. Тому нормалізуємо надійно — лишаємо тільки
// цифри й десяткову крапку (формат укр. фіскального чека: десятковий роздільник —
// крапка). Будь-яке сміття між розрядами зникає, а регекс поля лишається
// запобіжником. Застосовується і на автозаповненні з QR, і при ручному вводі.
export function normalizeReceiptSum(raw) {
  if (typeof raw !== 'string') return ''
  return raw.replace(/[^\d.]/g, '')
}

export function parseReceiptQrUrl(raw) {
  if (typeof raw !== 'string' || !raw.trim()) {
    return { ok: false, error: RECEIPT_QR_ERROR.NOT_URL, raw }
  }
  let url
  try {
    url = new URL(raw.trim())
  } catch (e) {
    return { ok: false, error: RECEIPT_QR_ERROR.NOT_URL, raw }
  }
  if (url.hostname !== EXPECTED_HOST) {
    return { ok: false, error: RECEIPT_QR_ERROR.WRONG_HOST, host: url.hostname, raw }
  }
  const values = {}
  for (const key of [...REQUIRED_PARAMS, ...OPTIONAL_PARAMS]) {
    const v = url.searchParams.get(key)
    if (v != null) values[key] = v
  }
  const missing = REQUIRED_PARAMS.filter(k => !values[k])
  if (missing.length) {
    return {
      ok: false,
      error: RECEIPT_QR_ERROR.MISSING_PARAMS,
      missing,
      fields: toUiFields(values, raw),
      raw,
    }
  }
  return { ok: true, fields: toUiFields(values, raw), raw }
}

// Сирий QR-рядок чека несе суму так, як її зашила каса — з пробілом-роздільником
// тисяч, нерозривним U+00A0 або навіть «mojibake» (`Â`+U+00A0). Ці символи лишаються
// і в повному URL. Бекендний `URLField` відхиляє URL із пробілом/не-ASCII у
// незакодованому вигляді, а помилка по полю `url` користувачу не показується (поля
// url на формі немає) — через це реєстрація падала з generic-помилкою саме на чеках
// із пробілами в сумі. Тому будуємо канонічний URL: переписуємо `sm` нормалізованим
// значенням і даємо URL API percent-енкодити решту, щоб лінк лишився валідним і
// поїхав у webhook клієнта чистим. Якщо рядок не парситься як URL — не відправляємо
// його (бекенд має всі поля чека явно, url опційний).
function canonicalReceiptUrl(raw, smRaw) {
  if (typeof raw !== 'string' || !raw.trim()) return ''
  try {
    const u = new URL(raw.trim())
    if (smRaw != null) u.searchParams.set('sm', normalizeReceiptSum(smRaw))
    return u.toString()
  } catch (e) {
    return ''
  }
}

function toUiFields(values, raw) {
  return {
    dateIso: values.date ? formatDateYmdToIso(values.date) : '',
    time: normalizeQrTime(values.time),
    fn: values.fn || '',
    id: values.id || '',
    sm: normalizeReceiptSum(values.sm),
    mac: values.mac || '',
    url: canonicalReceiptUrl(raw, values.sm),
  }
}

export function emptyReceiptFields() {
  return {
    dateIso: '',
    time: '',
    fn: '',
    id: '',
    sm: '',
    mac: '',
    url: '',
  }
}

export function ymdFromIso(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((iso || '').trim())
  if (!m) return ''
  return `${m[1]}${m[2]}${m[3]}`
}

export function dmyFromIso(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((iso || '').trim())
  if (!m) return iso || ''
  return `${m[3]}.${m[2]}.${m[1]}`
}
